import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  SLOT_PLANS,
  addDaysToIsoDate,
  formatExpiryDisplay,
  planDaysForPlan,
} from "constants/clientSlotPlans";
import { apiUrl } from "utils/apiUrl";
import { paymentApiUrl } from "utils/paymentApiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { loadPaymentConfig } from "utils/paymentConfig";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

function normalizePromo(raw) {
  return String(raw || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
}

function clampDiscountPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.min(90, Math.max(0, Math.round(x)));
}

function computeDiscountedUsd(regularUsd, discountPercent) {
  const r = Number(regularUsd);
  if (!Number.isFinite(r) || r <= 0) return 1;
  const pct = clampDiscountPercent(discountPercent);
  const paid = Math.round((r * (100 - pct)) / 100);
  return Math.max(1, paid);
}

function savingsUsd(plan, discountPercent) {
  const r = Number(plan?.priceUsdRegular);
  if (!Number.isFinite(r)) return 0;
  const paid = computeDiscountedUsd(r, discountPercent);
  return Math.max(0, Math.round(r - paid));
}

function formatUsd(n) {
  return `$${Math.round(Number(n))}`;
}

function checkoutSubscriptionPath(applied, searchParams) {
  const fromUrl = String(searchParams?.get("path") || "")
    .trim()
    .toLowerCase();
  if (fromUrl === "path1" || fromUrl === "path2") return fromUrl;
  if (applied?.subscriptionPath === "path1" || applied?.subscriptionPath === "path2") {
    return applied.subscriptionPath;
  }
  return undefined;
}

export default function ExtendSlot() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const slotNum = useMemo(() => {
    const n = Number(searchParams.get("slot"));
    return Number.isFinite(n) && n >= 1 ? n : null;
  }, [searchParams]);

  const slotName = useMemo(
    () => String(searchParams.get("reserve") || "").trim(),
    [searchParams],
  );

  const currentExpiringDate = useMemo(() => {
    const fromUrl = String(searchParams.get("currentExp") || "").trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(fromUrl) ? fromUrl : null;
  }, [searchParams]);

  const backTo = useMemo(() => {
    const path = checkoutSubscriptionPath(null, searchParams);
    if (path === "path1") return "/client/subscriptions/Path1BrokerPartner";
    if (path === "path2") return "/client/subscriptions/Path2PersonnalBroker";
    return "/client/subscription";
  }, [searchParams]);

  const [promoDraft, setPromoDraft] = useState(
    () => searchParams.get("promo") || "",
  );
  const [applied, setApplied] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [payError, setPayError] = useState(null);
  const [paymentCfg, setPaymentCfg] = useState(null);
  const autoPromoValidatedRef = useRef(false);

  useEffect(() => {
    void loadPaymentConfig().then(setPaymentCfg);
  }, []);

  const validatePromo = useCallback(
    async (rawCode) => {
      const trimmed = String(rawCode ?? "").trim();
      const pathForCheckout = checkoutSubscriptionPath(null, searchParams);
      if (!trimmed) {
        setPromoError(null);
        setPayError(null);
        setApplied(null);
        return false;
      }

      if (
        applied &&
        normalizePromo(trimmed) === normalizePromo(applied.code)
      ) {
        return true;
      }

      setPromoError(null);
      setPayError(null);
      setApplyingPromo(true);
      try {
        const validateBody = { code: trimmed };
        if (pathForCheckout) validateBody.subscriptionPath = pathForCheckout;
        const res = await fetch(apiUrl("/api/client/validate-slot-promo"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(validateBody),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          setApplied(null);
          setPromoError(
            clientSafeMessage(
              data.message,
              res.status === 401
                ? "Sign in to validate a promo code."
                : "This promo code is not valid.",
            ),
          );
          return false;
        }
        setApplied({
          code: data.code,
          discountPercent: data.discountPercent,
          kind: data.kind,
          subscriptionPath: data.subscriptionPath || null,
          pathLabel: data.pathLabel,
          source: data.source,
          infoMessage: data.message || "Promo code applied.",
        });
        setPromoDraft(String(data.code ?? trimmed ?? ""));
        return true;
      } catch (e) {
        setApplied(null);
        setPromoError(clientSafeMessage(e?.message, "Network error."));
        return false;
      } finally {
        setApplyingPromo(false);
      }
    },
    [applied, searchParams],
  );

  useEffect(() => {
    const fromUrl = String(searchParams.get("promo") || "").trim();
    if (!fromUrl || autoPromoValidatedRef.current) return;
    autoPromoValidatedRef.current = true;
    setPromoDraft(fromUrl);
    void validatePromo(fromUrl);
  }, [searchParams, validatePromo]);

  const promoApplied =
    Boolean(applied?.code) &&
    normalizePromo(promoDraft) === normalizePromo(applied.code);

  async function startExtensionPayment(planId) {
    if (!slotNum) return;
    setPayError(null);
    setLoadingPlanId(planId);
    const pathForCheckout = checkoutSubscriptionPath(applied, searchParams);
    try {
      const payBody = {
        planId,
        promo: applied?.code ?? "",
        extendSlot: true,
        extend: "1",
        slot: slotNum,
      };
      if (pathForCheckout) payBody.subscriptionPath = pathForCheckout;
      const cfg = paymentCfg || (await loadPaymentConfig());
      const payPath = cfg?.slotPaymentEndpoint || "/api/client/slot-payment";
      const res = await fetch(paymentApiUrl(payPath), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payBody),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setPayError(
          clientSafeMessage(
            data.message,
            res.status === 401
              ? "Please sign in to continue."
              : "Payment could not be started. Please try again.",
          ),
        );
        return;
      }
      navigate("/PayUSDT", {
        replace: false,
        state: {
          extendMode: true,
          extendSlot: slotNum,
          planId: data.planId || planId,
          promo: data.promo || applied?.code || null,
          discountPercent: data.discountPercent ?? applied?.discountPercent ?? 0,
          kind: applied?.kind || null,
          pathLabel: applied?.pathLabel || null,
          appliedMessage: applied?.infoMessage || null,
          regularPriceUsd: data.regularPriceUsd,
          priceUsd: data.priceUsd,
          planPriceUsd: data.planPriceUsd ?? data.priceUsd,
          sendAmountUsdt: data.sendAmountUsdt,
          walletSendBufferUsdt: data.walletSendBufferUsdt,
          plisioUnlockMinUsdt: data.plisioUnlockMinUsdt,
          bep20: data.bep20,
          mock: data.mock,
          slotName: slotName || data.slotOrderId || null,
          currentExpiringDate,
          subscriptionPath: pathForCheckout || null,
        },
      });
    } catch (e) {
      setPayError(clientSafeMessage(e?.message, "Network error."));
    } finally {
      setLoadingPlanId(null);
    }
  }

  if (!slotNum || !slotName) {
    return (
      <main className="min-h-screen bg-[#0B1437] px-5 py-16 text-center text-white">
        <p className="text-gray-300">Invalid extension link.</p>
        <Link to="/client/subscription" className="mt-4 inline-block text-brand-300 hover:underline">
          Back to Subscription
        </Link>
      </main>
    );
  }

  const accent = "text-brand-300";

  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-6xl space-y-8">
        <LegalPagesBrandHeader />

        <header className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">Extend subscription</h1>
          <p className="mt-2 font-mono text-lg font-semibold text-brand-200">{slotName}</p>
          {currentExpiringDate ? (
            <p className="mt-2 text-sm text-gray-300">
              Current expiration:{" "}
              <strong className="text-white">
                {formatExpiryDisplay(currentExpiringDate)}
              </strong>
            </p>
          ) : null}
          <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Choose any plan below — its duration is <strong className="text-white">added</strong> to
            your current expiration date. Pay in{" "}
            <strong className="text-white">USDT BEP20 (BSC)</strong>. Admin approval is required
            after payment, same as your first subscription.
          </p>
        </header>

        <section className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <label
            htmlFor="extend-promo"
            className="block text-xs font-semibold uppercase tracking-wide text-gray-400"
          >
            Promo code (optional)
          </label>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input
              id="extend-promo"
              type="text"
              value={promoDraft}
              onChange={(e) => {
                const v = e.target.value;
                setPromoDraft(v);
                if (!String(v).trim()) {
                  setPromoError(null);
                  setApplied(null);
                } else if (
                  applied &&
                  normalizePromo(v) !== normalizePromo(applied.code)
                ) {
                  setApplied(null);
                  setPromoError(null);
                }
              }}
              placeholder="Enter promo code"
              className="w-full flex-1 rounded-xl border border-white/15 bg-[#0a102c] px-4 py-3 font-mono text-sm text-white outline-none focus:border-brand-400"
            />
            <button
              type="button"
              disabled={applyingPromo || loadingPlanId != null || promoApplied}
              onClick={() => void validatePromo(promoDraft)}
              className={`shrink-0 rounded-xl px-5 py-3 text-sm font-bold text-white disabled:cursor-default ${
                promoApplied
                  ? "bg-emerald-600/90 disabled:opacity-100"
                  : "bg-brand-500 hover:bg-brand-600 disabled:opacity-50"
              }`}
            >
              {applyingPromo ? "Checking…" : promoApplied ? "Applied" : "Apply"}
            </button>
          </div>
          {promoError ? (
            <p className="mt-2 text-xs text-red-300" role="alert">
              {promoError}
            </p>
          ) : applied ? (
            <p className="mt-2 text-xs text-emerald-300">{applied.infoMessage}</p>
          ) : null}
        </section>

        {payError ? (
          <div
            className="rounded-xl border border-red-400/40 bg-red-950/50 px-4 py-3 text-center text-sm text-red-100"
            role="alert"
          >
            {payError}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          {SLOT_PLANS.map((plan) => {
            const hasApplied = Boolean(applied?.code);
            const pct = applied?.discountPercent ?? 0;
            const paidUsd = hasApplied
              ? computeDiscountedUsd(plan.priceUsdRegular, pct)
              : plan.priceUsdRegular;
            const save = hasApplied ? savingsUsd(plan, pct) : 0;
            const newExpiry = currentExpiringDate
              ? addDaysToIsoDate(currentExpiringDate, planDaysForPlan(plan))
              : null;

            return (
              <section
                key={plan.id}
                className={`flex flex-col rounded-2xl border border-white/10 bg-white/[0.06] p-6 shadow-xl ${plan.accent}`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  +{plan.days} days
                </p>
                <h2 className="mt-1 text-xl font-bold text-white">{plan.name}</h2>
                <p className="mt-1 text-xs text-gray-400">{plan.cadence}</p>
                {newExpiry ? (
                  <p className="mt-3 text-sm text-violet-200">
                    New expiration:{" "}
                    <strong className="text-white">{formatExpiryDisplay(newExpiry)}</strong>
                  </p>
                ) : (
                  <p className="mt-3 text-sm text-gray-400">
                    Adds {plan.days} days to your current subscription.
                  </p>
                )}
                <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  {hasApplied ? (
                    <>
                      <span className="text-sm text-gray-500 line-through">
                        {plan.regularPriceLabel}
                      </span>
                      <span className={`text-2xl font-bold tabular-nums ${accent}`}>
                        {formatUsd(paidUsd)}
                      </span>
                      <span className="rounded-md border border-amber-400/50 bg-amber-500/15 px-2 py-0.5 text-xs font-bold text-amber-100">
                        Save ${save} ({pct}%)
                      </span>
                    </>
                  ) : (
                    <span className="text-2xl font-bold tabular-nums text-gray-100">
                      {plan.regularPriceLabel}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={loadingPlanId != null}
                  onClick={() => void startExtensionPayment(plan.id)}
                  className="mt-6 w-full rounded-xl bg-brand-500 py-3 text-sm font-bold text-white transition hover:bg-brand-600 disabled:opacity-50"
                >
                  {loadingPlanId === plan.id
                    ? "Creating payment…"
                    : `Extend with ${plan.name}`}
                </button>
              </section>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-400">
          <Link to={backTo} className="text-brand-300 hover:underline">
            Back to subscription
          </Link>
        </p>

        <footer className="border-t border-white/10 pt-6 text-center text-xs text-gray-400">
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
